import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';

interface ProceedAckFooterProps {
  blockingCount: number;
  attentionCount: number;
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmIcon?: ReactNode;
  cancelLabel?: string;
}

// Posting-modal footer with a built-in "second confirmation" (86d3hm2vr):
//   • blocking lines present  → primary disabled with a reason (can't post unpostable lines).
//   • attention lines present → a required ack checkbox gates the primary.
//   • otherwise               → plain confirm.
export function ProceedAckFooter({
  blockingCount,
  attentionCount,
  isSubmitting = false,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmIcon,
  cancelLabel = 'Back',
}: ProceedAckFooterProps) {
  const [acked, setAcked] = useState(false);

  const hasBlocking = blockingCount > 0;
  const needsAck = !hasBlocking && attentionCount > 0;
  const canConfirm = !isSubmitting && !hasBlocking && (attentionCount === 0 || acked);

  return (
    <div className="space-y-3">
      {hasBlocking && (
        <p className="text-xs text-destructive">
          {blockingCount} line{blockingCount === 1 ? '' : 's'} can't be posted. Resolve{' '}
          {blockingCount === 1 ? 'it' : 'them'} before creating.
        </p>
      )}

      {needsAck && (
        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-sm">
          <Checkbox
            checked={acked}
            onCheckedChange={(v) => setAcked(v === true)}
            disabled={isSubmitting}
            className="mt-0.5"
          />
          <span>
            I've reviewed the {attentionCount} flagged line{attentionCount === 1 ? '' : 's'} and want
            to post anyway.
          </span>
        </label>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {cancelLabel}
        </Button>
        <Button onClick={onConfirm} disabled={!canConfirm} isLoading={isSubmitting}>
          {!isSubmitting && confirmIcon}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}
